import { useEffect, useRef } from 'react';
import useGetGeneralAppData from '../../hooks/useGetGeneralAppData';
import { HEARTBEAT_INTERVAL_MS, PRESENCE_EVENTS } from './consult-presence.constants';
import { useConsultPresence } from './ConsultPresenceProvider';
import { ConsultPresenceErrorEvent } from './consult-presence.types';

export function useConsultPresenceDetail(consultCode: number | string | undefined) {
  const { socket, isEnabled, currentUserId } = useConsultPresence();
  const { userName } = useGetGeneralAppData();
  const joinedConsultRef = useRef<number | string | null>(null);

  useEffect(() => {
    if (!isEnabled || !socket || !consultCode || !userName) {
      return undefined;
    }

    const joinPayload = {
      consultCode,
      username: userName,
      userId: currentUserId,
    };

    const joinConsult = () => {
      socket.emit(PRESENCE_EVENTS.JOIN_CONSULT, joinPayload);
      joinedConsultRef.current = consultCode;
    };

    joinConsult();

    const heartbeatId = setInterval(() => {
      socket.emit(PRESENCE_EVENTS.HEARTBEAT, { consultCode });
    }, HEARTBEAT_INTERVAL_MS);

    const handleError = (payload: ConsultPresenceErrorEvent) => {
      console.error('[consult-presence:detail]', payload.message);
      if (joinedConsultRef.current === consultCode) {
        joinConsult();
      }
    };

    const handleReconnect = () => {
      if (joinedConsultRef.current === consultCode) {
        joinConsult();
      }
    };

    socket.on(PRESENCE_EVENTS.ERROR, handleError);
    socket.io.on('reconnect', handleReconnect);

    return () => {
      clearInterval(heartbeatId);
      socket.off(PRESENCE_EVENTS.ERROR, handleError);
      socket.io.off('reconnect', handleReconnect);
      socket.emit(PRESENCE_EVENTS.LEAVE_CONSULT);
      joinedConsultRef.current = null;
    };
  }, [consultCode, currentUserId, isEnabled, socket, userName]);
}
